import type { PodCrossCheck as PodCrossCheckData } from '../api/client'
import { theme } from '../theme'

/**
 * DID THIS BATCH MEASURE THE POD IT SAYS IT MEASURED?
 *
 * WHY THIS COMPONENT EXISTS
 *
 * B2.6 built the cross-check — the pod we deliberately relayed to against the pod eFax's own
 * `J2MsgID` reported — and nothing rendered it. `GET /api/batches/{id}` returned the whole
 * answer and this page threw it away, so a mis-attributed batch was visible only to somebody
 * who opened the batch and read the JSON by hand.
 *
 * That is worse than a missing number, and the asymmetry is the whole point. The purpose of
 * an internal E2E is to measure ONE NAMED POD. If we relay to `ccs3` and eFax says `ccs1`,
 * every figure on this page is about a different pod than the operator believes — and it
 * still looks authoritative, so somebody drains a pod on the strength of it. A missing
 * reading gets investigated. A wrong one gets acted on.
 *
 * IT IS A DIAGNOSTIC AND NEVER A VERDICT, AND THIS COMPONENT MUST NOT BLUR THAT
 *
 * A fax that went out and came back PASSED, whichever pod carried it. `pod.py`'s contract is
 * that losing attribution must never cost a measurement. So nothing here renders a pass or a
 * fail, nothing here is coloured like one, and the mismatch panel says in words that the
 * SLA verdicts are unaffected — because the obvious misreading of a red panel on a batch
 * report is "these results are void", and acting on that discards good data.
 *
 * NOT IN `BatchStatus.tsx`, AND THAT WAS A JUDGEMENT RATHER THAN AN OVERSIGHT
 *
 * That module is documented as the shared batch CONTAINER STATUS badge "for every page that
 * lists batches", both its exports take a batch summary row, and
 * `tests/test_batch_status_is_shared.py` pins both list pages to it. A pod mismatch is not a
 * container status — forcing it into `StatusBadge`'s vocabulary would put a value in that
 * badge which `BATCH_CONTAINER_STATUSES` cannot hold, and the guard checking that map against
 * the engine in both directions would be right to fail. It is also detail-only data: the
 * listing endpoint does not carry it, deliberately, because computing it per row costs a
 * query per row. Putting it in the module the list pages share would invite exactly that.
 *
 * AN UNRECOGNISED OUTCOME IS RENDERED VISIBLY UNRECOGNISED, for the reason `BatchStatus.tsx`
 * documents at length: the outcome vocabulary is declared in Python
 * (`backend/services/agent_dispatch.py`), and a vocabulary that lives on the other side of an
 * HTTP boundary is exactly the setup where a surface quietly stops recognising a value. A
 * fourth outcome must not render as blank or as one we know.
 */

/** The outcomes this surface has a treatment for. Mirrors `POD_CHECK_OUTCOMES`. */
const KNOWN_OUTCOMES = ['agreement', 'mismatch', 'not_recorded'] as const

const COLOURS: Record<string, string> = {
  agreement: '#4caf50',
  // ORANGE, NOT RED. Red on a batch report reads as "this measurement failed", which is the
  // one thing a mismatch does not mean.
  mismatch: '#ffa726',
  not_recorded: '#9aa0a6',
}

/** Magenta appears in no treatment above, and the border is dashed, so the signal does not
 * depend on telling two colours apart. */
const UNRECOGNISED = '#e040fb'

const LABELS: Record<string, string> = {
  agreement: 'Pod confirmed',
  mismatch: 'Pod MISMATCH',
  not_recorded: 'Pod not yet confirmed',
}

export function podCrossCheckIsKnown(outcome: string): boolean {
  return (KNOWN_OUTCOMES as readonly string[]).includes(outcome)
}

/**
 * The one-line indicator. Rendered for every internal batch, not only a mismatching one: an
 * operator who sees nothing cannot tell "confirmed" from "this page does not check".
 */
export function PodCrossCheckBadge({ outcome }: { outcome: string }) {
  const known = podCrossCheckIsKnown(outcome)
  const colour = known ? COLOURS[outcome] : UNRECOGNISED
  const label = known ? LABELS[outcome] : `⚠ ${outcome || '(blank)'}`
  return (
    <span
      title={
        known
          ? undefined
          : `This page does not recognise the pod cross-check outcome ${
              outcome ? `"${outcome}"` : '(blank)'
            }. It is shown as the server sent it. The vocabulary is declared in ` +
            'backend/services/agent_dispatch.py.'
      }
      style={{
        background: colour + '22',
        color: colour,
        border: `1px ${known ? 'solid' : 'dashed'} ${colour}${known ? '55' : 'cc'}`,
        borderRadius: 3,
        padding: '0.1rem 0.45rem',
        fontSize: '0.72rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )
}

/**
 * The full panel for a batch report.
 *
 * RENDERS NOTHING FOR A PUBLIC-PATH BATCH. No pod was ever aimed at one, so there is no
 * intent for an observation to disagree with, and a "not recorded" row on every public batch
 * report would train an operator to ignore the one place this matters. The predicate is the
 * send path, never `origin`.
 */
export function PodCrossCheckPanel({ check }: { check: PodCrossCheckData | null | undefined }) {
  if (!check) return null
  if (check.send_path !== 'internal') return null

  const mismatch = check.outcome === 'mismatch'
  const splitAcrossPods = check.observed_pods.length > 1
  const colour = podCrossCheckIsKnown(check.outcome)
    ? COLOURS[check.outcome]
    : UNRECOGNISED

  return (
    <div
      style={{
        background: theme.bgElevated,
        border: `1px ${mismatch ? 'solid' : 'solid'} ${colour}${mismatch ? '88' : '33'}`,
        borderLeft: `3px solid ${colour}`,
        borderRadius: 6,
        padding: '0.6rem 0.8rem',
        marginBottom: '1rem',
        fontSize: '0.78rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
        <PodCrossCheckBadge outcome={check.outcome} />
        <span style={{ color: theme.textMuted }}>
          targeted <strong style={{ color: theme.text }}>{check.target_pod || '(none)'}</strong>
          {check.target_environment ? ` / ${check.target_environment}` : ''}
          {' · '}
          {check.observed_pods.length > 0
            ? `eFax reported ${check.observed_pods.join(', ')}`
            : 'eFax has not reported a pod yet'}
        </span>
        <span style={{ color: theme.textMuted, fontSize: '0.72rem' }}>
          {check.agreement} confirmed · {check.mismatch} mismatched · {check.not_recorded} not
          yet confirmed, of {check.items}
        </span>
      </div>

      {mismatch && (
        <div style={{ marginTop: '0.45rem', color: '#ffa726' }}>
          <strong>
            {splitAcrossPods
              ? 'This batch was split across more than one pod.'
              : 'This batch did not measure the pod it names.'}
          </strong>{' '}
          We relayed to {check.target_pod || '(none)'} and eFax says{' '}
          {check.observed_pods.join(', ')} handled {check.mismatch === 1 ? 'one fax' : `${check.mismatch} faxes`}.
          The timings above are therefore about {splitAcrossPods ? 'more than one pod' : 'a different pod'} than
          this report names.
          {' '}
          {/* THE SENTENCE THAT STOPS GOOD DATA BEING DISCARDED. An orange panel on a batch
              report reads as "void" unless it says otherwise in words. */}
          <span style={{ color: theme.textMuted }}>
            The SLA verdicts are unaffected: these faxes went out and came back, and passed or
            failed on their own timings whichever pod carried them. What is in doubt is which
            pod the numbers describe, not whether the numbers are real.
          </span>
        </div>
      )}

      {mismatch && check.mismatched_items.length > 0 && (
        <div style={{ marginTop: '0.45rem' }}>
          <table style={{ fontSize: '0.72rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: theme.textMuted, textAlign: 'left' }}>
                <th style={{ padding: '0.15rem 0.6rem 0.15rem 0' }}>#</th>
                <th style={{ padding: '0.15rem 0.6rem 0.15rem 0' }}>run token</th>
                <th style={{ padding: '0.15rem 0.6rem 0.15rem 0' }}>pod eFax reported</th>
                <th style={{ padding: '0.15rem 0 0.15rem 0' }}>J2MsgID</th>
              </tr>
            </thead>
            <tbody>
              {check.mismatched_items.map(item => (
                <tr key={item.run_token ?? `${item.batch_seq}`}>
                  <td style={{ padding: '0.15rem 0.6rem 0.15rem 0', color: theme.textMuted }}>
                    {item.batch_seq ?? '—'}
                  </td>
                  <td style={{ padding: '0.15rem 0.6rem 0.15rem 0', fontFamily: 'monospace' }}>
                    {item.run_token ?? '—'}
                  </td>
                  <td style={{ padding: '0.15rem 0.6rem 0.15rem 0' }}>
                    {item.observed_pod ?? '—'}
                    {item.observed_environment ? ` / ${item.observed_environment}` : ''}
                  </td>
                  <td style={{ padding: '0.15rem 0 0.15rem 0', fontFamily: 'monospace', color: theme.textMuted }}>
                    {item.observed_msg_id ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {check.outcome === 'not_recorded' && (
        <div style={{ marginTop: '0.45rem', color: theme.textMuted }}>
          Nothing has been compared yet. The pod eFax used is read out of its own send
          notification email, which arrives minutes after the relay — so this is the normal
          state for a batch that has just launched, and it is not a finding about either pod.
        </div>
      )}

      {/* STATED, NOT OMITTED. A panel that showed a pod row and no region row would leave a
          reader to assume the regions were compared and agreed. */}
      {!check.region_compared && (
        <div style={{ marginTop: '0.4rem', color: '#666', fontSize: '0.7rem' }}>
          The region is not compared: {check.region_not_compared_because}
        </div>
      )}
    </div>
  )
}
